/**
 * English: Refer and earn
 *
 * What this file covers: The referral programme: links, referrals, earnings and withdrawals.
 *
 * Tone: Upbeat and friendly, and honest about the terms and when money is paid.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const refer: Catalog = {
  'refer.eyebrow': 'Partner programme',
  'refer.title': 'Refer and earn',
  'refer.description': 'Introduce a business to RepletoryReach and earn a share of what we earn on everything they use.',
  'refer.activeDescription': 'You earn {{percent}}% of what RepletoryReach earns on everything your referrals use, for as long as they stay.',
  'refer.loadFailed': 'Your partner details could not be loaded.',
  'refer.closed': 'The partner programme is not open at the moment. Please check back later.',
  'refer.closedAccount': 'This partner account has been closed.',
  'refer.suspended': 'Your partner account is on hold, so nothing new is being earned.',
  'refer.notEnrolled.title': 'You are not in the partner programme yet',
  'refer.notEnrolled.body': 'Open Refer and earn to read how it works and apply.',
  'refer.how.title': 'How it works',
  'refer.how.step1': 'Apply, and we come back to you once someone has read it.',
  'refer.how.step2': 'Share your link or your code with a business you think would use this.',
  'refer.how.step3': 'Every time they use the platform you earn {{percent}}% of what RepletoryReach earns on it, for as long as they stay with us.',
  'refer.how.step4': 'Earnings become available after {{days}} days and you can withdraw anything over {{minimum}} to your bank, or turn it into credits on your own account.',
  'refer.enrol.title': 'Apply to the programme',
  'refer.enrol.description': 'For people and businesses in South Africa: earnings are paid into a South African bank account. A few details, and someone will read it and come back to you either way.',
  'refer.enrol.displayName': 'Name to be known by',
  'refer.enrol.displayNameHint': 'What a business you refer will see, and what we call you in our own records.',
  'refer.enrol.businessName': 'Business name, if you trade as one',
  'refer.enrol.contactEmail': 'Email for programme messages',
  'refer.enrol.contactEmailHint': 'Where we write about your earnings and your withdrawals.',
  'refer.enrol.contactPhone': 'Phone number',
  'refer.enrol.website': 'Website or social page',
  'refer.enrol.how': 'How will you introduce us?',
  'refer.enrol.howHint': 'A sentence or two. This is what someone reads before approving your application.',
  'refer.enrol.acceptTerms': 'I accept the partner terms',
  'refer.enrol.readTerms': 'Read the terms',
  'refer.enrol.hideTerms': 'Hide the terms',
  'refer.enrol.version': 'version {{version}}',
  'refer.enrol.submit': 'Send my application',
  'refer.enrol.sent': 'Your application is with us. We will be in touch.',
  'refer.enrol.failed': 'Your application could not be sent.',
  'refer.pending.title': 'Your application is with us',
  'refer.pending.body': 'Someone will read it and come back to you. Your link is issued the moment it is approved.',
  'refer.share.title': 'Your link and your code',
  'refer.share.description': 'Anyone who signs up through these is recorded as yours.',
  'refer.share.linkLabel': 'Your link',
  'refer.share.copyCode': 'Copy the code',
  'refer.share.copyLink': 'Copy the link',
  'refer.share.copied': 'Copied',
  'refer.share.whatsapp': 'Share on WhatsApp',
  'refer.share.message': 'I use RepletoryReach for WhatsApp with customers. Sign up here and it is set up in a few minutes: {{link}}',
  'refer.metric.available': 'Available to withdraw',
  'refer.metric.availableHint': 'Cleared, and yours to take.',
  'refer.metric.pending': 'Still clearing',
  'refer.metric.pendingHint': 'Becomes available {{days}} days after the day it was earned.',
  'refer.metric.lifetime': 'Earned altogether',
  'refer.metric.lifetimeHint': 'Everything you have ever earned, before withdrawals.',
  'refer.metric.referrals': 'Businesses referred',
  'refer.metric.referralsHint': 'Still earning for you.',
  'refer.metric.withdrawn': 'Paid out',
  'refer.metric.withdrawnHint': 'What has already reached you.',
  'refer.recent.title': 'Your latest earnings',
  'refer.recent.all': 'See all',
  'refer.recent.emptyTitle': 'Nothing earned yet',
  'refer.recent.emptyBody': 'Share your link. Earnings appear the day after a business you referred uses the platform.',
  'refer.referrals.title': 'Businesses you referred',
  'refer.referrals.description': 'Who signed up through you, and what each has earned you.',
  'refer.referrals.loadFailed': 'Your referrals could not be loaded.',
  'refer.referrals.emptyTitle': 'No referrals yet',
  'refer.referrals.emptyBody': 'Share your link with a business and they appear here as soon as they sign up.',
  'refer.referrals.noneMatch': 'Nothing under that filter',
  'refer.referrals.noneMatchBody': 'Try another one, or see them all.',
  'refer.earnings.title': 'Your earnings',
  'refer.earnings.description': 'One line per business per day, plus withdrawals and any corrections.',
  'refer.earnings.loadFailed': 'Your earnings could not be loaded.',
  'refer.earnings.emptyTitle': 'Nothing here yet',
  'refer.earnings.emptyBody': 'Earnings appear the day after a business you referred uses the platform.',
  'refer.earnings.noneMatch': 'Nothing under that filter',
  'refer.earnings.noneMatchBody': 'Try another one, or see them all.',
  'refer.withdrawals.title': 'Withdrawals',
  'refer.withdrawals.description': 'Take your earnings to your bank, or turn them into credits on this account.',
  'refer.withdrawals.historyTitle': 'Every withdrawal',
  'refer.withdrawals.loadFailed': 'Your withdrawals could not be loaded.',
  'refer.withdrawals.emptyTitle': 'Nothing withdrawn yet',
  'refer.withdrawals.emptyBody': 'Once you have earnings available you can ask for them here.',
  'refer.withdrawals.oneAtATime': 'You have a withdrawal waiting. Once it is paid or cancelled you can ask for another.',
  'refer.withdrawals.cancelTitle': 'Cancel this withdrawal?',
  'refer.withdrawals.cancelBody': '{{amount}} goes back into your available earnings and you can ask again whenever you like.',
  'refer.withdrawals.cancelConfirm': 'Cancel it',
  'refer.withdrawals.cancelled': 'Cancelled. The amount is back in your available earnings.',
  'refer.withdrawals.cancelFailed': 'That withdrawal could not be cancelled.',
  'refer.withdraw.open': 'Withdraw',
  'refer.withdraw.title': 'Withdraw your earnings',
  'refer.withdraw.available': 'You have {{amount}} available.',
  'refer.withdraw.amount': 'How much',
  'refer.withdraw.amountHint': 'At least {{minimum}}.',
  'refer.withdraw.belowMinimum': 'The smallest withdrawal is {{minimum}}.',
  'refer.withdraw.tooMuch': 'That is more than you have available.',
  'refer.withdraw.method': 'How you want it',
  'refer.withdraw.eft': 'To my bank account',
  'refer.withdraw.eftHint': 'Paid to {{account}} within ten working days of being approved.',
  'refer.withdraw.eftNeedsAccount': 'Add a bank account under Withdrawals, Bank account, and this becomes available.',
  'refer.withdraw.credits': 'As credits on this account',
  'refer.withdraw.creditsHint': 'Added to your balance straight away, to spend on messaging.',
  'refer.withdraw.requestAction': 'Ask for this withdrawal',
  'refer.withdraw.convertAction': 'Turn it into credits',
  'refer.withdraw.requested': 'Your withdrawal is in. We will write when it is paid.',
  'refer.withdraw.converted': 'Added to your balance.',
  'refer.withdraw.failed': 'That withdrawal could not be made.',
  'refer.payout.title': 'Bank account',
  'refer.payout.description': 'Where a bank withdrawal is paid. Only you and our billing team can see it.',
  'refer.payout.add': 'Add an account',
  'refer.payout.change': 'Change it',
  'refer.payout.locked': 'The account cannot change while a withdrawal is waiting.',
  'refer.payout.loadFailed': 'Your bank account could not be loaded.',
  'refer.payout.emptyTitle': 'No account saved',
  'refer.payout.emptyBody': 'Add one and you can withdraw straight to your bank.',
  'refer.payout.holder': 'Account holder',
  'refer.payout.holderHint': 'The name on the account, as the bank has it.',
  'refer.payout.bank': 'Bank',
  'refer.payout.branch': 'Branch code',
  'refer.payout.branchHint': 'Six digits.',
  'refer.payout.number': 'Account number',
  'refer.payout.numberHint': 'Digits only. We show you only the last four afterwards.',
  'refer.payout.type': 'Type of account',
  'refer.payout.typeCheque': 'Cheque or current',
  'refer.payout.typeSavings': 'Savings',
  'refer.payout.typeTransmission': 'Transmission',
  'refer.payout.saved': 'Your bank account is saved.',
  'refer.payout.failed': 'Those bank details could not be saved.',
  'refer.column.day': 'Day',
  'refer.column.company': 'Business',
  'refer.column.kind': 'What',
  'refer.column.detail': 'Detail',
  'refer.column.amount': 'Amount',
  'refer.column.clears': 'Available',
  'refer.column.since': 'Since',
  'refer.column.status': 'Status',
  'refer.column.firstUsage': 'First used',
  'refer.column.notYet': 'Not yet',
  'refer.column.earned': 'Earned',
  'refer.column.requested': 'Asked for',
  'refer.column.method': 'Paid how',
  'refer.column.reference': 'Reference',
  'refer.company.unnamed': 'A referred business',
  'refer.cleared': 'Now',
  'refer.filter.all': 'All',
  'refer.filter.earning': 'Earning',
  'refer.filter.ended': 'Ended',
  'refer.filter.revoked': 'Taken away',
  'refer.status.earning': 'Earning',
  'refer.status.ended': 'Ended',
  'refer.status.revoked': 'Taken away',
  'refer.kind.commission': 'Your share',
  'refer.kind.adjustment': 'Correction',
  'refer.kind.withdrawal': 'Withdrawal',
  'refer.kind.withdrawal_reversal': 'Withdrawal returned',
  'refer.method.eft': 'Bank payment',
  'refer.method.wallet_credit': 'Credits',
  'refer.withdrawalStatus.requested': 'Waiting',
  'refer.withdrawalStatus.approved': 'Approved',
  'refer.withdrawalStatus.paid': 'Paid',
  'refer.withdrawalStatus.rejected': 'Not approved',
  'refer.withdrawalStatus.cancelled': 'Cancelled',
  'refer.enrol.southAfricaOnly': 'The partner programme is open to partners in South Africa only, because earnings are paid into a South African bank account.',
  'refer.enrol.country': 'Where are you based?',
  'refer.enrol.countryHint': 'The programme is for partners in South Africa: earnings are paid into a South African bank account.',
  'refer.enrol.countryChoose': 'Choose a country',
  'refer.enrol.countryZa': 'South Africa',
  'refer.enrol.countryOther': 'Somewhere else'
};

export default refer;
